/**
 * `createPreviewRenderer` (W2-01a preview-gl; contract render/types.ts `PreviewRenderer`, ARCHITECTURE.md §7.3,
 * rendering.md R§8).
 *
 * Draws one FrameDrawList exactly like Phira, in Phira's composition order:
 *   1. background: black, blurred illustration over the whole window, black 0.3, viewport dim x res.alpha;
 *   2. videos (P1, RenderOptions.videos);
 *   3. line bodies in (zOrder, index) order, paint buffers composited right after their line;
 *   4. note buckets (one draw call each);
 *   5. non-global effects (steps 1-4 render into a window-sized FBO; each effect ping-pongs over the viewport);
 *   6. Bad-note ghosts of judged play (after the non-global effects, like Phira);
 *   7. hit-effect sprites, then square particles;
 *   8. HUD;
 *   9. global effects over everything.
 * Straight alpha (SRC_ALPHA, ONE_MINUS_SRC_ALPHA, destination alpha kept), textures bilinear without mipmaps.
 * Phira's MSAA setting renders into a 4x multisampled renderbuffer that is resolved before effects and at the end;
 * without effects and MSAA the frame goes straight to the canvas.
 *
 * Context: WebGL2, alpha false, premultipliedAlpha false, no antialias (gl/context.ts). On loss every GPU object is
 * dropped; on restore everything is re-created from the CPU-side caches (skin and media ImageBitmaps, text rasters,
 * the engine's paint stamps) and `onContextRestored` listeners run.
 *
 * The contract covers availability, the render-scale mode with automatic lowering (renderScale.ts), the HUD text
 * measurer for the engine's legacy layout, invalidation callbacks (a font subset arrived, media finished decoding,
 * text was deferred, a video seek finished) and the frame statistics. `createPreviewRendererGl` adds a few
 * diagnostics of this implementation (applied effects, deferred text, the last stats, the scale mode).
 */

import type { Engine, FrameDrawList } from '../../core/engine/types';
import type { HudTextMeasurer } from '../../core/engine/notes/index';
import type { EffectId } from '../../core/model/types';
import type { Unsubscribe } from '../../core/util/types';
import { ContextWatcher, createGlContext, GpuTimer, readCaps, type GlCaps } from '../gl/context';
import { clipFromPx, GlState } from '../gl/glState';
import { buildProgramOrThrow } from '../gl/program';
import { QuadBatch } from '../gl/quadBatch';
import { blitColor, MsaaTarget, PingPong, type ColorTarget } from '../gl/renderTargets';
import { COPY_FRAGMENT, DISC_FRAGMENT, QUAD_ATTRIBS, QUAD_FRAGMENT, QUAD_VERTEX } from '../gl/shaders';
import { setUnpackDefaults, TextureCache } from '../gl/textures';
import type {
  CreatePreviewRenderer,
  LoadedSkin,
  PreviewRenderer,
  RenderOptions,
  RenderStats,
  TextureProvider,
} from '../types';
import { BUILTIN_SHADERS } from './effects/builtin/index';
import { EffectRunner } from './effects/runner';
import { drawBackground } from './backgroundPass';
import { PreviewFonts } from './fonts';
import { drawHitFx } from './fxPass';
import { drawHud } from './hudPass';
import { drawLines } from './linePass';
import { drawNotes } from './notePass';
import { PaintBuffers } from './paintPass';
import { spaceMatrices, viewportPxInt, type PassContext, type PassPrograms } from './passContext';
import { perfMessages, perfOverlayActive, perfText, registerPerfSource } from '../frame/perfOverlay';
import { backingSize, RenderScaleGovernor, type RenderScaleMode } from './renderScale';
import { TextCache } from './textCache';
import { drawVideos, VideoSync } from './videoPass';

/** Frame statistics with this implementation's diagnostics (tests, the dev overlay). */
export interface PreviewRenderStats extends RenderStats {
  readonly appliedEffects: readonly EffectId[];
  /** Some text was skipped or drawn stale this frame (another frame follows by itself). */
  readonly textDeferred: boolean;
}

/** The preview renderer with this implementation's diagnostics. */
export interface PreviewRendererGl extends PreviewRenderer {
  render(list: FrameDrawList, engine: Engine, opts: RenderOptions): PreviewRenderStats;
  readonly renderScaleMode: RenderScaleMode;
  readonly measureHudText: HudTextMeasurer;
  readonly lastStats: PreviewRenderStats;
}

interface Gpu {
  readonly gl: WebGL2RenderingContext;
  readonly caps: GlCaps;
  readonly state: GlState;
  readonly batch: QuadBatch;
  readonly programs: PassPrograms;
  readonly textures: TextureCache;
  readonly pp: PingPong;
  readonly msaa: MsaaTarget;
  readonly paint: PaintBuffers;
  readonly effects: EffectRunner;
  readonly timer: GpuTimer | null;
}

const EMPTY_STATS: PreviewRenderStats = {
  cpuMs: 0,
  drawCalls: 0,
  quads: 0,
  shaderErrors: [],
  gpuMs: null,
  renderScale: 1,
  notPreviewedEffects: [],
  pendingEffects: [],
  appliedEffects: [],
  videosDrawn: 0,
  textDeferred: false,
};

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function createGpu(gl: WebGL2RenderingContext): Gpu {
  const caps = readCaps(gl);
  setUnpackDefaults(gl);
  const state = new GlState(gl);
  const batch = new QuadBatch(gl, state);
  const programs: PassPrograms = {
    quad: buildProgramOrThrow(gl, QUAD_VERTEX, QUAD_FRAGMENT, QUAD_ATTRIBS, 'quad'),
    disc: buildProgramOrThrow(gl, QUAD_VERTEX, DISC_FRAGMENT, QUAD_ATTRIBS, 'disc'),
    copy: buildProgramOrThrow(gl, QUAD_VERTEX, COPY_FRAGMENT, QUAD_ATTRIBS, 'copy'),
  };
  for (const p of [programs.quad, programs.disc, programs.copy]) {
    const loc = p.uniform('u_tex');
    if (loc !== null) {
      state.useProgram(p.program);
      gl.uniform1i(loc, 0);
    }
  }
  return {
    gl,
    caps,
    state,
    batch,
    programs,
    textures: new TextureCache(gl),
    pp: new PingPong(gl),
    msaa: new MsaaTarget(gl),
    paint: new PaintBuffers(gl, state),
    effects: new EffectRunner(gl, state, batch, () => BUILTIN_SHADERS),
    timer: caps.timer === null ? null : new GpuTimer(gl, caps.timer),
  };
}

function disposeGpu(g: Gpu): void {
  g.timer?.dispose();
  g.effects.dispose();
  g.paint.dispose();
  g.msaa.dispose();
  g.pp.dispose();
  g.textures.dispose();
  g.batch.dispose();
  for (const p of [g.programs.quad, g.programs.disc, g.programs.copy]) g.gl.deleteProgram(p.program);
}

class GlPreviewRenderer implements PreviewRendererGl {
  readonly canvas: HTMLCanvasElement;
  private readonly gl: WebGL2RenderingContext | null;
  private gpu: Gpu | null = null;
  private failureText: string | null = null;
  private readonly watcher: ContextWatcher | null;
  private readonly fonts = new PreviewFonts();
  private readonly text: TextCache;
  private readonly videos: VideoSync;
  private readonly governor = new RenderScaleGovernor();
  private skin: LoadedSkin | null = null;
  private provider: TextureProvider | null = null;
  private providerOff: Unsubscribe | null = null;
  private readonly restoredListeners = new Set<() => void>();
  private readonly invalidateListeners = new Set<() => void>();
  private invalidatePending = false;
  private css: readonly [number, number, number] = [0, 0, 1];
  /** CSS size of the last applied backing store (a change clears the governor's verdict). */
  private appliedCss: readonly [number, number, number] = [0, 0, 0];
  private sizeDirty = false;
  /** Chart time of the previous frame (a frame at the same time is a still frame). */
  private lastT = Number.NaN;
  private stats: PreviewRenderStats = EMPTY_STATS;
  private disposed = false;
  /** Removes this renderer's line from the dev performance overlay. */
  private perfOff: (() => void) | null = null;
  readonly measureHudText: HudTextMeasurer;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.text = new TextCache(this.fonts);
    this.videos = new VideoSync(() => {
      this.invalidate();
    });
    this.measureHudText = this.fonts.measureHudText;
    this.fonts.onChange(() => {
      this.invalidate();
    });
    this.text.onEvict((c) => this.gpu?.textures.release(c));
    if (perfOverlayActive()) {
      this.perfOff = registerPerfSource('preview', () => {
        const st = this.stats;
        return perfText(perfMessages.preview, {
          cpu: st.cpuMs.toFixed(1),
          gpu: st.gpuMs === null ? perfText(perfMessages.none) : st.gpuMs.toFixed(1),
          scale: st.renderScale.toFixed(2),
          quads: st.quads,
          w: this.canvas.width,
          h: this.canvas.height,
        });
      });
    }
    const gl = createGlContext(canvas);
    this.gl = gl;
    if (gl === null) {
      this.failureText = 'WebGL2 is not available in this browser';
      this.watcher = null;
      return;
    }
    this.watcher = new ContextWatcher(
      canvas,
      () => {
        this.onLost();
      },
      () => {
        this.onRestored();
      },
    );
    this.initGpu();
  }

  private initGpu(): void {
    const gl = this.gl;
    if (gl === null) return;
    try {
      this.gpu = createGpu(gl);
      this.text.setMaxSide(this.gpu.caps.maxTextureSize);
      this.failureText = null;
    } catch (e) {
      this.gpu = null;
      this.failureText = e instanceof Error ? e.message : String(e);
    }
  }

  private onLost(): void {
    // Every GPU object is gone with the context: forget them without deleting.
    this.gpu?.effects.reset(false);
    this.gpu?.paint.forget();
    this.gpu = null;
  }

  private onRestored(): void {
    if (this.disposed) return;
    this.initGpu();
    for (const cb of [...this.restoredListeners]) cb();
    this.invalidate();
  }

  get available(): boolean {
    return this.gl !== null && this.failureText === null;
  }

  get failure(): string | null {
    return this.failureText;
  }

  get contextLost(): boolean {
    return this.watcher?.lost ?? false;
  }

  get renderScale(): number {
    return this.governor.factor;
  }

  get renderScaleMode(): RenderScaleMode {
    return this.governor.mode;
  }

  get lastStats(): PreviewRenderStats {
    return this.stats;
  }

  setRenderScaleMode(mode: RenderScaleMode): void {
    if (!this.governor.setMode(mode)) return;
    this.applySize();
    this.invalidate();
  }

  onContextRestored(cb: () => void): Unsubscribe {
    this.restoredListeners.add(cb);
    return () => {
      this.restoredListeners.delete(cb);
    };
  }

  onInvalidate(cb: () => void): Unsubscribe {
    this.invalidateListeners.add(cb);
    return () => {
      this.invalidateListeners.delete(cb);
    };
  }

  /** Notifies invalidation listeners once, after the current call stack (never from inside `render`). */
  private invalidate(): void {
    if (this.invalidatePending || this.disposed) return;
    this.invalidatePending = true;
    queueMicrotask(() => {
      this.invalidatePending = false;
      if (this.disposed) return;
      for (const cb of [...this.invalidateListeners]) cb();
    });
  }

  resize(cssW: number, cssH: number, dpr: number): void {
    this.css = [cssW, cssH, dpr];
    this.canvas.style.width = `${String(Math.max(0, cssW))}px`;
    this.canvas.style.height = `${String(Math.max(0, cssH))}px`;
    this.applySize();
  }

  /**
   * Applies the backing-store size. Only plain size assignments, and only when the size really changes: resizing a
   * WebGL canvas makes the browser wait for the GPU, so the governor skips the next frames' timings (UX-028).
   */
  private applySize(): void {
    this.sizeDirty = false;
    const [cssW, cssH, dpr] = this.css;
    const [w, h] = backingSize(cssW, cssH, dpr, this.governor.factor);
    if (this.canvas.width === w && this.canvas.height === h) return;
    const prev = this.appliedCss;
    const cssChanged = prev[0] !== cssW || prev[1] !== cssH || prev[2] !== dpr;
    this.appliedCss = this.css;
    if (this.canvas.width !== w) this.canvas.width = w;
    if (this.canvas.height !== h) this.canvas.height = h;
    this.governor.noteResize(cssChanged);
  }

  setSkin(skin: LoadedSkin): void {
    if (this.skin === skin) return;
    const old = this.skin;
    this.skin = skin;
    const tex = this.gpu?.textures;
    if (old !== null && tex !== undefined) {
      for (const img of Object.values(old.images)) tex.release(img);
      if (old.holdBody !== null) tex.release(old.holdBody);
      if (old.holdBodyMh !== null) tex.release(old.holdBodyMh);
    }
    this.invalidate();
  }

  setTextures(t: TextureProvider): void {
    if (this.provider === t) return;
    this.providerOff?.();
    this.provider = t;
    this.providerOff = t.onReady(() => {
      this.invalidate();
    });
    this.gpu?.textures.releaseUnpinned();
    this.invalidate();
  }

  render(list: FrameDrawList, engine: Engine, opts: RenderOptions): PreviewRenderStats {
    const t0 = now();
    const gl = this.gl;
    if (gl === null) {
      this.clear2d();
      return (this.stats = EMPTY_STATS);
    }
    if (this.watcher?.lost === true) return (this.stats = { ...EMPTY_STATS, renderScale: this.governor.factor });
    if (gl.isContextLost()) {
      this.watcher?.markLost();
      return (this.stats = { ...EMPTY_STATS, renderScale: this.governor.factor });
    }
    const g = this.gpu;
    if (g === null) return (this.stats = EMPTY_STATS);
    // A still frame (paused: the chart time did not move) draws at full resolution (UX-028).
    const still = list.t === this.lastT;
    this.lastT = list.t;
    if (this.governor.beforeFrame(still, t0)) this.sizeDirty = true;
    if (this.sizeDirty) this.applySize();
    const stats = this.drawFrame(g, list, engine, opts);
    const cpuMs = now() - t0;
    const gpuMs = g.timer?.latestMs ?? null;
    const t1 = now();
    if (this.governor.sample(cpuMs + (gpuMs ?? 0), t1)) {
      // Resizing clears the canvas: do it right before the next frame is drawn, not after this one was presented.
      this.sizeDirty = true;
      this.invalidate();
    }
    if (stats.textDeferred) this.invalidate();
    this.stats = { ...stats, cpuMs, gpuMs, renderScale: this.governor.factor };
    return this.stats;
  }

  private clear2d(): void {
    const ctx = this.canvas.getContext('2d');
    if (ctx === null) return;
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
  }

  private drawFrame(g: Gpu, list: FrameDrawList, engine: Engine, opts: RenderOptions): PreviewRenderStats {
    const { gl, state, batch } = g;
    g.textures.beginFrame();
    this.text.beginFrame();
    g.effects.beginFrame();
    batch.resetStats();
    g.timer?.begin();
    state.reset();

    const tw = Math.max(1, gl.drawingBufferWidth || this.canvas.width);
    const th = Math.max(1, gl.drawingBufferHeight || this.canvas.height);
    const nonGlobal = opts.effects ? list.effects : [];
    const global = opts.effects ? list.globalEffects : [];
    const msaa =
      opts.msaa && g.caps.msaaSamples > 0 && tw <= g.caps.maxRenderbufferSize && th <= g.caps.maxRenderbufferSize;
    const offscreen = msaa || nonGlobal.length > 0 || global.length > 0;

    let inMsaa = false;
    let color: ColorTarget | null = null;
    let fbo: WebGLFramebuffer | null = null;
    if (msaa) {
      g.msaa.ensure(tw, th, g.caps.msaaSamples);
      fbo = g.msaa.fbo;
      inMsaa = true;
    } else if (offscreen) {
      g.pp.ensure(tw, th);
      color = g.pp.a;
      fbo = color.fbo;
    }
    if (offscreen) g.pp.ensure(tw, th);
    state.bindTarget(fbo, tw, th);

    const vp = list.viewport;
    const m = spaceMatrices(vp, tw, th);
    const ctx: PassContext = {
      gl,
      state,
      batch,
      programs: g.programs,
      textures: g.textures,
      text: this.text,
      fonts: this.fonts,
      list,
      vp,
      targetFbo: fbo,
      targetW: tw,
      targetH: th,
      kx: m.kx,
      ky: m.ky,
      pxToClip: m.pxToClip,
      worldToClip: m.worldToClip,
      uiToClip: m.uiToClip,
      pxPerUnitX: (vp.W / 2) * m.kx,
      pxPerUnitY: (vp.W / 2) * m.ky,
      provider: this.provider,
      skin: this.skin,
    };

    /** The frame as a single-sampled colour target (resolving the MSAA buffer when needed). */
    const resolve = (): ColorTarget => {
      batch.flush();
      if (inMsaa) {
        state.setScissor(null);
        blitColor(gl, g.msaa.fbo, g.pp.a.fbo, tw, th);
        state.forgetTarget();
        return g.pp.a;
      }
      return color ?? g.pp.a;
    };
    /** Makes `src` the frame being drawn into (copied back into the MSAA buffer in the MSAA path). */
    const continueIn = (src: ColorTarget): void => {
      if (inMsaa) {
        state.bindTarget(g.msaa.fbo, tw, th);
        this.copy(g, src, tw, th);
        fbo = g.msaa.fbo;
      } else {
        color = src;
        fbo = src.fbo;
        state.bindTarget(fbo, tw, th);
      }
      ctx.targetFbo = fbo;
    };

    drawBackground(ctx);
    const videosDrawn = drawVideos(ctx, this.videos, opts.videos, now());
    drawLines(ctx, g.paint, engine);
    drawNotes(ctx, 'notes');
    batch.flush();

    if (nonGlobal.length > 0) {
      const src = resolve();
      const out = g.effects.chain(
        nonGlobal,
        src,
        g.pp,
        { targetW: tw, targetH: th, viewport: viewportPxInt(ctx) },
        this.provider,
      );
      if (out !== src || !inMsaa) continueIn(out);
      else state.bindTarget(fbo, tw, th);
    }

    drawNotes(ctx, 'badGhosts');
    drawHitFx(ctx);
    drawHud(ctx);
    batch.flush();

    let final: ColorTarget | null = color;
    if (global.length > 0) {
      const src = resolve();
      final = g.effects.chain(global, src, g.pp, { targetW: tw, targetH: th, viewport: null }, this.provider);
    } else if (inMsaa) final = resolve();

    if (offscreen && final !== null) {
      state.bindTarget(null, tw, th);
      this.copy(g, final, tw, th);
    }
    batch.flush();
    g.timer?.end();

    const report = g.effects.frameReport;
    const seen = new Set<string>();
    const shaderErrors = report.shaderErrors.filter((e) => {
      const k = `${e.asset}\u0000${e.log}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    return {
      cpuMs: 0,
      drawCalls: batch.drawCalls,
      quads: batch.quads,
      shaderErrors,
      gpuMs: null,
      renderScale: this.governor.factor,
      notPreviewedEffects: report.notPreviewed,
      pendingEffects: report.pending,
      appliedEffects: report.applied,
      videosDrawn,
      textDeferred: this.text.deferred,
    };
  }

  /** Copies a colour target over the whole bound target (no blending). */
  private copy(g: Gpu, src: ColorTarget, tw: number, th: number): void {
    const b = g.batch;
    b.setProgram(g.programs.copy);
    b.setBlend('none');
    b.setTransform(clipFromPx(tw, th));
    b.setTexture(src.tex);
    // The image top is stored in the texture's last row.
    b.rect([1, 0, 0, 1, 0, 0], 0, 0, tw, th, 0, 1, 1, 0, 255, 255, 255, 255);
    b.flush();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.perfOff?.();
    this.perfOff = null;
    this.providerOff?.();
    this.providerOff = null;
    this.videos.dispose();
    this.text.clear();
    this.fonts.dispose();
    if (this.gpu !== null && this.watcher?.lost !== true) disposeGpu(this.gpu);
    this.gpu = null;
    this.watcher?.dispose();
    this.restoredListeners.clear();
    this.invalidateListeners.clear();
  }
}

/** The preview renderer with its extras (availability, render scale, invalidation, HUD text measurer). */
export function createPreviewRendererGl(canvas: HTMLCanvasElement): PreviewRendererGl {
  return new GlPreviewRenderer(canvas);
}

export const createPreviewRenderer: CreatePreviewRenderer = (canvas) => createPreviewRendererGl(canvas);
